import { TemplateResult, LitElement, html } from 'lit';
import { customElement, property, queryAssignedElements } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import encapsulatedStyles from './styles/outline-content-image-block.encapsulated.css?inline';
import globalStyles from './styles/outline-content-image-block.global.css?inline';
import { screenSizeMap } from '../../controllers/screen-size-controller';
import { validateSlot } from '../../utilities/slot';

export const contentImagePositions = ['left', 'right'] as const;
export type ContentImagePosition = (typeof contentImagePositions)[number];

export const contentImageVariants = ['default', 'simple'] as const;
export type ContentImageVariant = (typeof contentImageVariants)[number];

const componentName = 'outline-content-image-block';
/**
 * Outline Content Image Block component
 * 
 * @slot eyebrow - the eyebrow of the component
 * @slot image - The image of the component
 * @slot heading - The heading of the component
 * @slot content - The content of the component
 * @slot ctas - The call to action buttons of the component
 * @property {string} imagePosition - The position of the image
 */

@customElement('outline-content-image-block')
export class OutlineContentImageBlock extends LitElement {
  resizeObserver: ResizeObserver;
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    encapsulatedCSS: encapsulatedStyles,
    globalCSS: globalStyles,
  });

  /**
   * Ref to content in slot 'eyebrow'
   */
  @queryAssignedElements({slot: 'eyebrow'})
  eyebrowItems: Array<HTMLElement>;

  /**
   * Ref to content in slot 'ctas'
   */
  @queryAssignedElements({slot: 'ctas'})
  ctaItems: Array<HTMLElement>;

  /**
   * Ref to content in slot 'date-1'
   */
  @queryAssignedElements({slot: 'date-1'})
  date1Ref: Array<HTMLElement>;  
  
  /**
   * Ref to content in slot 'date-2'
   */
  @queryAssignedElements({slot: 'date-2'})
  date2Ref: Array<HTMLElement>;  
  
  /**
   * Ref to content in slot 'supplemental'
   */
  @queryAssignedElements({slot: 'supplemental'})
  supplementRef: Array<HTMLElement>;  

  /**
   * Sets image position to `left` or `right`
   */
  @property({ type: String, attribute: 'image-position' })
  imagePosition: ContentImagePosition = 'left';

  /**
   * Component variant: 'default' for styled card variant, 'simple' for less styled version for events.
   */
  @property({type: String })
  variant: ContentImageVariant = 'default';

  @property({ type: String, reflect: true, attribute: 'images-content-container' })
  imageContentContainer = '';

  /**
   * If screen is below md width.
   */
  @property({ type: Boolean, reflect: true, attribute: 'is-mobile' })
  isMobile: boolean;

  /**
   * If image should be below text at mobile sizes.
   */
  @property({ type: Boolean, reflect: true, attribute: 'mobile-reverse' })
  mobileReverse: boolean;

  /**
   * Ref to this.
   */
  _selfRef: HTMLElement;

  connectedCallback() {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(() => {
      this.resizeAtScreen();
    });
  };

  firstUpdated() {
    this.setSelfRef()
    if (this.variant === 'default') {
      validateSlot(this.eyebrowItems, '.eyebrow', this.shadowRoot);
      validateSlot(this.ctaItems, '.ctas', this.shadowRoot);
    }
    if (this.variant === 'simple') {
      validateSlot(this.date1Ref, '.simple-content-date-1', this.shadowRoot);
      validateSlot(this.date2Ref, '.simple-content-date-2', this.shadowRoot);
      validateSlot(this.supplementRef, '.simple-content-supplemental', this.shadowRoot);
      validateSlot(this.ctaItems, '.ctas', this.shadowRoot);
    }
    this.resizeObserver.observe(this._selfRef);
  }

  setSelfRef() {
    const self = this.shadowRoot?.querySelector('.content-image-block') as HTMLElement;
    if (self) {      
      this._selfRef = self;
    }
  }

  resizeAtScreen() {
    this.isMobile = this._selfRef.clientWidth < (screenSizeMap.get('md') as number);    
  }

  render(): TemplateResult {
    const imageOrientation = this.setImageOrientation();
    return html`<div class="content-image-block ${this.imageContentContainer}">
        ${imageOrientation === 'left' || imageOrientation === 'top'? this.ImageTemplate() : null }
        ${this.ContentTemplate()}
        ${imageOrientation === 'right' || imageOrientation === 'bottom' ? this.ImageTemplate() : null }
    </div>`;
  }

  setImageOrientation() {
    if (!this.isMobile){
      return this.imagePosition;
    }
    if (this.isMobile) {
      if (this.mobileReverse) {
        return 'bottom'
      } else {
        return 'top'
      }
    } 
  }

  ImageTemplate(): TemplateResult {
    return html`<div class="image-container">
        <slot name="image"></slot>
    </div>`;
  }

  ContentTemplate() {    
    return this.variant==='default' 
      ? this.DefaultContentTemplate() 
      : this.SimpleContentTemplate()
  }

  DefaultContentTemplate(): TemplateResult {
    return html`<div class="content-container">
        <div class="eyebrow">
          <slot name="eyebrow"></slot>
        </div>
        <slot name="heading"></slot>
        <slot name="content"></slot>
        <div class="ctas">
          <slot name="ctas"></slot>
        </div>
    </div>`;
  }

  SimpleContentTemplate() {
    return html `
      <div class="simple-content-container">
        <div class="simple-content-info">
          <div class="simple-content-date-1">
            <slot name="date-1"></slot>
          </div>
          <div class="simple-content-date-2">
            <slot name="date-2"></slot>
          </div>
          <div class="simple-content-supplemental">
            <slot name="supplemental"></slot>
          </div>
        </div>
        <div class="simple-content-ctas">
          <slot name="ctas"></slot>
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [componentName]: OutlineContentImageBlock;
  }
}
